import {
  AppShell,
  Avatar,
  AvatarFallback,
  Button,
  ColumnHintsProvider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SuperAdminBanner,
  SyncIndicator,
  cn,
  initials,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, ClipboardCheck, ClipboardList, Inbox, LogOut, Plus, Settings, Shield, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';


import { fetchMe, logout } from '../../api/auth.js';
import { fetchUnreadCount } from '../../api/notifications.js';
import { fetchViolationDashboard } from '../../api/violations.js';
import { fetchOrgSettings } from '../../api/org-settings.js';
import { uploadFile } from '../../api/uploads.js';
import { useBrandingStore } from '../../stores/branding-store.js';
import { useSyncStore } from '../../stores/sync-store.js';

import { startSyncEngine, stopSyncEngine } from '../../offline/sync-engine.js';
import { api as apiClient, ApiError } from '../../lib/api-client.js';
import { getOfflineDb, type QueueItem, wipeOfflineDbForUser } from '../../offline/db.js';
import { getPhoto, markPhotoUploaded } from '../../offline/photos.js';

import { CommandPaletteTrigger, OrgCommandPalette } from './command-palette.js';
import { NotificationBell } from './notifications/bell-drawer.js';
import { PwaUpdateToast } from './notifications/pwa-update-toast.js';
import { SyncConflictBanner } from './sync/conflict-banner.js';

const NAV = [
  { to: '/dashboard', label: 'Today', icon: Sun, end: true },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/buildings', label: 'Buildings', icon: Building2 },
  { to: '/work-orders', label: 'Work orders', icon: ClipboardList },
  { to: '/inspections', label: 'Inspections', icon: ClipboardCheck },
  { to: '/compliance', label: 'Compliance', icon: Shield },
  { to: '/violations', label: 'Violations', icon: AlertTriangle },
];

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  building_manager: 'Building manager',
  maintenance: 'Maintenance staff',
  case_manager: 'Case manager',
  auditor: 'Auditor',
  vendor: 'Vendor',
};

function sectionTitle(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return 'Today';
  if (pathname.startsWith('/inbox') || pathname.startsWith('/notifications') || pathname.startsWith('/ai')) {
    return 'Inbox';
  }
  if (pathname.startsWith('/buildings') || pathname.startsWith('/units')) return 'Buildings';
  if (pathname.startsWith('/work-orders')) return 'Work orders';
  if (pathname.startsWith('/inspections')) return 'Inspections';
  if (pathname.startsWith('/compliance') || pathname.startsWith('/lead')) return 'Compliance';
  if (pathname.startsWith('/violations')) return 'Violations';
  if (pathname.startsWith('/settings')) return 'Settings';
  if (pathname.startsWith('/reminders') || pathname.startsWith('/tasks')) return 'Tasks';
  return 'SiteWise';
}

export function OrgLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const branding = useBrandingStore((s) => s.snapshot);
  const syncState = useSyncStore((s) => s.state);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
  });
  const settingsQuery = useQuery({
    queryKey: ['org-settings'],
    queryFn: fetchOrgSettings,
  });
  const showColumnHints = settingsQuery.data?.settings.display.showColumnHints ?? true;

  const isSuperAdminViewing = meQuery.data?.user.isSuperAdmin === true;
  const isAdminUser = meQuery.data?.user.role === 'admin';
  const isTabletMode = meQuery.data?.user.tabletMode === true;

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    enabled: !isTabletMode,
    refetchInterval: 30_000,
  });
  const violationsNavQuery = useQuery({
    queryKey: ['violations', 'dashboard'],
    queryFn: fetchViolationDashboard,
    enabled: !isTabletMode,
    staleTime: 60_000,
  });
  const openViolations =
    violationsNavQuery.data?.openByAgency.reduce((sum, row) => sum + row.count, 0) ?? 0;

  useEffect(() => {
    if (branding?.orgName) {
      document.title = isTabletMode
        ? `${branding.orgName} · Work orders`
        : `${branding.orgName} · SiteWise`;
    }
  }, [branding, isTabletMode]);

  // Phase 10 — start the sync engine for the signed-in user. Stops on
  // unmount so logout / surface switch doesn't keep firing requests.
  const userIdForSync = meQuery.data?.user.id ?? null;
  useEffect(() => {
    if (!userIdForSync) return undefined;
    startSyncEngine(userIdForSync, {
      uploadPhotos: uploadQueuedPhotos,
      sendMutation: async (item: QueueItem) => {
        try {
          await apiClient(item.endpoint, {
            method: item.method,
            body: item.body,
            idempotencyKey: item.idempotencyKey,
          });
        } catch (err) {
          if (err instanceof ApiError) {
            throw Object.assign(new Error(err.message), {
              status: err.status,
              details: err.details,
            });
          }
          throw err;
        }
      },
    });
    return () => stopSyncEngine();
  }, [userIdForSync]);

  async function handleLogout() {
    const userId = meQuery.data?.user.id ?? null;
    stopSyncEngine();
    await logout();
    if (userId) {
      await wipeOfflineDbForUser(userId);
    }
    navigate('/login', { replace: true });
  }

  // Tablet wall logins only see work orders.
  const onWorkOrderRoute =
    location.pathname === '/work-orders' || location.pathname.startsWith('/work-orders/');
  if (isTabletMode && !onWorkOrderRoute) {
    return <Navigate to="/work-orders" replace />;
  }

  return (
    <AppShell
      isSuperAdmin={isSuperAdminViewing}
      topBanner={
        <>
          {isSuperAdminViewing ? (
            <SuperAdminBanner
              viewingOrgName={branding?.orgName}
              onExit={async () => {
                await logout();
                navigate('/admin/dashboard', { replace: true });
              }}
            />
          ) : null}
          <SyncConflictBanner />
        </>
      }
      sidebarHeader={
        <Link
          to={isTabletMode ? '/work-orders' : '/dashboard'}
          className="flex min-w-0 items-center gap-2.5"
        >
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt="" className="h-[30px] w-[30px] shrink-0 rounded-lg object-cover" />
          ) : (
            <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-accent text-[11px] font-bold text-accent-fg">
              {branding?.orgName ? initials(branding.orgName) : '·'}
            </div>
          )}
          <span className="hidden min-w-0 xl:block">
            <span className="block truncate text-sm font-semibold leading-tight text-sidebar-fg">
              {branding?.orgName ?? 'Workspace'}
            </span>
            <span className="block truncate text-[11px] leading-tight text-sidebar-muted">
              {isTabletMode ? 'Wall display' : 'Portfolio'}
            </span>
          </span>
        </Link>
      }
      topHeader={
        isTabletMode ? (
          <div className="flex w-full items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Work orders</div>
              <div className="text-xs text-fg-muted">
                Wall display — open to view. Close from a phone login.
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden text-sm font-semibold text-fg sm:block">
              {sectionTitle(location.pathname)}
            </div>
            <div className="flex flex-1 justify-end sm:justify-center">
              <CommandPaletteTrigger
                onClick={() => {
                  setPaletteOpen(true);
                  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
                }}
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">New</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onSelect={() => navigate('/work-orders?new=1')}>
                    New work order
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate('/reminders')}>
                    Voice memo
                  </DropdownMenuItem>
                  {isAdminUser ? (
                    <DropdownMenuItem onSelect={() => navigate('/buildings/new')}>
                      New building
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
              {meQuery.data ? <NotificationBell /> : null}
            </div>
          </>
        )
      }
      nav={
        isTabletMode ? (
          <ul className="space-y-1">
            <li>
              <SideNavLink to="/work-orders" icon={ClipboardList} label="Work orders" />
            </li>
          </ul>
        ) : (
          <ul className="flex h-full flex-col gap-0.5">
            {NAV.map((item) => (
              <li key={item.to}>
                <SideNavLink
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  end={item.end}
                  badge={
                    item.to === '/inbox'
                      ? unreadQuery.data?.count
                      : item.to === '/violations'
                        ? openViolations
                        : undefined
                  }
                  badgeTone={item.to === '/violations' ? 'danger' : 'accent'}
                />
              </li>
            ))}
            <li className="mt-auto pt-2">
              <SideNavLink to="/settings" icon={Settings} label="Settings" />
            </li>
          </ul>
        )
      }
      sidebarFooter={
        meQuery.data ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left hover:bg-sidebar-hover max-xl:justify-center"
              >
                <Avatar className="h-[30px] w-[30px] shrink-0">
                  <AvatarFallback className="bg-sidebar-hover text-[11px] text-sidebar-fg">
                    {initials(meQuery.data.user.name || meQuery.data.user.email)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden min-w-0 flex-1 xl:block">
                  <span className="block truncate text-[13px] font-medium text-sidebar-fg">
                    {meQuery.data.user.name || meQuery.data.user.email}
                  </span>
                  <span className="block truncate text-[11px] text-sidebar-muted">
                    {ROLE_LABELS[meQuery.data.user.role] ?? meQuery.data.user.role}
                  </span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold">{meQuery.data.user.name}</div>
                <div className="text-xs text-fg-muted">{meQuery.data.user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/settings/profile')}>
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/settings/security')}>
                Security
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleLogout} className="text-danger">
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null
      }
      overlays={
        isTabletMode ? (
          <PwaUpdateToast />
        ) : (
          <>
            <SyncIndicator state={syncState} />
            <PwaUpdateToast />
            <OrgCommandPalette key={`${paletteOpen}-${location.pathname}`} />
          </>
        )
      }
    >
      <ColumnHintsProvider enabled={showColumnHints}>
        <Outlet />
      </ColumnHintsProvider>
    </AppShell>
  );
}

async function uploadQueuedPhotos(item: QueueItem): Promise<void> {
  if (!Array.isArray(item.photoIds) || item.photoIds.length === 0) return;
  if (!item.body || typeof item.body !== 'object') {
    throw new Error('Queued mutation is missing a valid JSON body');
  }

  const uploadIds = new Set<string>();
  const body = item.body as Record<string, unknown>;
  const existing = body.attachmentUploadIds;
  if (Array.isArray(existing)) {
    for (const value of existing) {
      if (typeof value === 'string' && value.length > 0) uploadIds.add(value);
    }
  }

  for (const photoId of item.photoIds) {
    const photo = await getPhoto(photoId);
    if (!photo) {
      throw new Error(`Missing offline photo ${photoId}`);
    }
    if (photo.uploadId) {
      uploadIds.add(photo.uploadId);
      continue;
    }
    const file = new File([photo.blob], photo.filename, {
      type: photo.contentType || 'application/octet-stream',
    });
    const uploaded = await uploadFile(file, 'work_order_photo');
    await markPhotoUploaded(photo.id, uploaded.uploadId);
    uploadIds.add(uploaded.uploadId);
  }

  const updatedBody = {
    ...body,
    attachmentUploadIds: Array.from(uploadIds),
  };
  item.body = updatedBody;
  item.photoIds = [];
  await getOfflineDb().queueItems.update(item.id, {
    body: updatedBody,
    photoIds: [],
    updatedAt: Date.now(),
  });
}

function SideNavLink({
  to,
  icon: Icon,
  label,
  badge,
  badgeTone = 'accent',
  end,
}: {
  to: string;
  icon: typeof Sun;
  label: string;
  badge?: number;
  badgeTone?: 'accent' | 'danger';
  end?: boolean;
}) {
  const { pathname } = useLocation();
  return (
    <NavLink
      to={to}
      end={end ?? false}
      title={label}
      className={({ isActive }) => {
        const active = isActive || (to === '/compliance' && pathname.startsWith('/lead'));
        return cn(
          'relative flex h-10 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition-colors',
          'max-xl:justify-center max-xl:px-0',
          active
            ? 'bg-sidebar-hover text-accent'
            : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg',
        );
      }}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span className="hidden truncate xl:inline">{label}</span>
      {badge != null && badge > 0 ? (
        <span
          className={cn(
            'ml-auto hidden min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold xl:inline-flex',
            badgeTone === 'danger' ? 'bg-danger text-white' : 'bg-accent text-accent-fg',
          )}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </NavLink>
  );
}
